import { mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  GET_NOTI_SET,
  GET_NOTI_SETTING_FOR_WEB,
  NOTI_SET,
  UPD_NOTI_SETTING_FOR_WEB,
  UTAGE_WEB_POLLING,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  InAppToastSettingsResponse,
  NotificationChannelSettingsResponse,
  NotificationSettingData,
  UpdateInAppToastRequest,
  UpdateMessageNotificationRequest,
  UpdatePreferenceMatchAlertRequest,
  UtagePollingItem,
} from "./types";

export const UTAGE_WEB_POLLING_TAKE = 5;

export const utagePollingQueryOptions = (timeStamp: string) => {
  return queryOptions({
    queryKey: [...queryKeys.notifications.all, "utage_web_polling", timeStamp],
    queryFn: () =>
      apiClient<UtagePollingItem[]>(UTAGE_WEB_POLLING, {
        take: UTAGE_WEB_POLLING_TAKE,
        time_stamp: timeStamp,
      }),
    staleTime: 0,
  });
};

const mergeSettings = (
  channelSettings: NotificationChannelSettingsResponse,
  inAppSettings: InAppToastSettingsResponse,
): NotificationSettingData => {
  return {
    userId: channelSettings.userId,
    appChannelEnabled: channelSettings.channels.app,
    emailChannelEnabled: channelSettings.channels.email,
    enableInAppNotification: inAppSettings.setting?.chat === 1,
    preferenceMatchAlertEnabled: inAppSettings.setting?.noti_pref_match !== 0,
    notiLike: inAppSettings.setting?.noti_like ?? 1,
    notiBookmarkedUser: inAppSettings.setting?.noti_bookmarked_user ?? 0,
    updatedAt: channelSettings.updatedAt,
  };
};

export const notificationSettingsQueryOptions = queryOptions({
  queryKey: queryKeys.notifications.settings(),
  queryFn: async () => {
    const [channelSettings, inAppSettings] = await Promise.all([
      apiClient<NotificationChannelSettingsResponse>(GET_NOTI_SETTING_FOR_WEB),
      apiClient<InAppToastSettingsResponse>(GET_NOTI_SET),
    ]);

    return mergeSettings(channelSettings, inAppSettings);
  },
});

export const updateInAppToastMutationOptions = mutationOptions({
  mutationFn: ({ enabled }: UpdateInAppToastRequest) => {
    const current = queryClient.getQueryData<NotificationSettingData>(
      queryKeys.notifications.settings(),
    );
    return apiClient<unknown>(NOTI_SET, {
      chat: enabled ? 1 : 0,
      noti_buzz: 0,
      andg_alt: 0,
      noti_chk_out: enabled ? 1 : 0,
      noti_like: enabled ? 1 : 0,
      noti_bookmarked_user: 0,
      // キャッシュ未取得時は送らない（未送信ならBEが現在値を保持する）
      ...(current && {
        noti_pref_match: current.preferenceMatchAlertEnabled ? 1 : 0,
      }),
    });
  },
  onMutate: async ({ enabled }) => {
    await queryClient.cancelQueries({
      queryKey: queryKeys.notifications.settings(),
    });

    const previous = queryClient.getQueryData<NotificationSettingData>(
      queryKeys.notifications.settings(),
    );

    if (previous) {
      queryClient.setQueryData<NotificationSettingData>(
        queryKeys.notifications.settings(),
        { ...previous, enableInAppNotification: enabled },
      );
    }

    return { previous };
  },
  onError: (_error, _variables, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.notifications.settings(),
        context.previous,
      );
    }
  },
  onSettled: () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.notifications.settings(),
    });
  },
});

export const updatePreferenceMatchAlertMutationOptions = mutationOptions({
  // noti_like / noti_bookmarked_user は未送信だとBEが強制上書きするため現在値を同梱する
  mutationFn: ({ enabled }: UpdatePreferenceMatchAlertRequest) => {
    const current = queryClient.getQueryData<NotificationSettingData>(
      queryKeys.notifications.settings(),
    );
    return apiClient<unknown>(NOTI_SET, {
      noti_pref_match: enabled ? 1 : 0,
      noti_like: current?.notiLike ?? 1,
      noti_bookmarked_user: current?.notiBookmarkedUser ?? 0,
    });
  },
  onMutate: async ({ enabled }) => {
    await queryClient.cancelQueries({
      queryKey: queryKeys.notifications.settings(),
    });

    const previous = queryClient.getQueryData<NotificationSettingData>(
      queryKeys.notifications.settings(),
    );

    if (previous) {
      queryClient.setQueryData<NotificationSettingData>(
        queryKeys.notifications.settings(),
        { ...previous, preferenceMatchAlertEnabled: enabled },
      );
    }

    return { previous };
  },
  onError: (_error, _variables, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.notifications.settings(),
        context.previous,
      );
    }
  },
  onSettled: () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.notifications.settings(),
    });
  },
});

export const updateMessageNotificationMutationOptions = mutationOptions({
  mutationFn: ({
    appChannelEnabled,
    emailChannelEnabled,
  }: UpdateMessageNotificationRequest) =>
    apiClient<unknown>(UPD_NOTI_SETTING_FOR_WEB, {
      app_channel: appChannelEnabled,
      email_channel: emailChannelEnabled,
      message_from_women_policy: "all",
      site_announcement_enabled: false,
    }),
  onMutate: async (variables) => {
    await queryClient.cancelQueries({
      queryKey: queryKeys.notifications.settings(),
    });

    const previous = queryClient.getQueryData<NotificationSettingData>(
      queryKeys.notifications.settings(),
    );

    if (previous) {
      queryClient.setQueryData<NotificationSettingData>(
        queryKeys.notifications.settings(),
        {
          ...previous,
          appChannelEnabled: variables.appChannelEnabled,
          emailChannelEnabled: variables.emailChannelEnabled,
        },
      );
    }

    return { previous };
  },
  onError: (_error, _variables, context) => {
    if (context?.previous) {
      queryClient.setQueryData(
        queryKeys.notifications.settings(),
        context.previous,
      );
    }
  },
  onSettled: () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.notifications.settings(),
    });
  },
});
